const API_BASE_URL = (process.env.REACT_APP_API_URL || 'http://localhost:3000/api').replace(/\/$/, '');
const ACCESS_TOKEN_KEY = 'onego_access_token';
const REFRESH_TOKEN_KEY = 'onego_refresh_token';

const storage = {
  getAccessToken() {
    return localStorage.getItem(ACCESS_TOKEN_KEY);
  },
  getRefreshToken() {
    return localStorage.getItem(REFRESH_TOKEN_KEY);
  },
  setTokens(tokens = {}) {
    if (tokens.accessToken) {
      localStorage.setItem(ACCESS_TOKEN_KEY, tokens.accessToken);
    }

    if (tokens.refreshToken) {
      localStorage.setItem(REFRESH_TOKEN_KEY, tokens.refreshToken);
    }
  },
  clearTokens() {
    localStorage.removeItem(ACCESS_TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
  },
};

async function parseJsonResponse(response) {
  const rawText = await response.text();
  const contentType = response.headers.get('content-type') || '';

  if (rawText && (rawText.trimStart().startsWith('<') || !contentType.includes('json'))) {
    if (response.status === 404) {
      throw new Error(
        'Route API introuvable (404). Redémarrez le backend après `npm run build` dans le dossier backend.',
      );
    }
    throw new Error(
      `Réponse invalide du serveur (${response.status}). Ouvrez le site sur http://localhost:3001 (Docker) ou vérifiez REACT_APP_API_URL et que l’API Academia tourne (port 3000).`,
    );
  }

  let payload = {};
  if (rawText) {
    try {
      payload = JSON.parse(rawText);
    } catch {
      throw new Error('Réponse serveur illisible (JSON attendu).');
    }
  }

  if (!response.ok) {
    const err = new Error(payload.message || 'Une erreur est survenue lors de la requête.');
    if (payload.code) err.code = payload.code;
    if (payload.data !== undefined) err.data = payload.data;
    err.httpStatus = response.status;
    throw err;
  }

  return payload;
}

/** Un seul renouvellement JWT à la fois (évite de saturer /auth/refresh-token sur 401 parallèles). */
let refreshAccessTokenPromise = null;

async function refreshAccessToken() {
  if (refreshAccessTokenPromise) {
    return refreshAccessTokenPromise;
  }

  refreshAccessTokenPromise = (async () => {
    const refreshToken = storage.getRefreshToken();

    if (!refreshToken) {
      throw new Error('Session expirée. Veuillez vous reconnecter.');
    }

    const response = await fetch(`${API_BASE_URL}/auth/refresh-token`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ refreshToken }),
    });

    const payload = await parseJsonResponse(response);

    if (payload.data?.tokens) {
      storage.setTokens(payload.data.tokens);
    }

    return payload;
  })().finally(() => {
    refreshAccessTokenPromise = null;
  });

  return refreshAccessTokenPromise;
}

/** CGU & confidentialité (GET public, sans JWT). */
export async function fetchPublicLegalDocuments() {
  const response = await apiRequest('/settings/legal', { method: 'GET', withAuth: false });
  return response.data;
}

/** Identité plateforme : nom, slogan, logo, support (GET public). */
export async function fetchPublicBranding() {
  const response = await apiRequest('/settings/branding', { method: 'GET', withAuth: false });
  return response.data;
}

export async function getNotifications(params = {}) {
  const page = params.page ?? 1;
  const limit = params.limit ?? 20;
  const status = params.status;
  const type = params.type;
  const dateFrom = params.dateFrom;
  const dateTo = params.dateTo;
  const query = new URLSearchParams();
  query.set('page', String(page));
  query.set('limit', String(limit));
  if (status) {
    query.set('status', String(status));
  }
  if (type) {
    query.set('type', String(type));
  }
  if (dateFrom) {
    query.set('dateFrom', String(dateFrom));
  }
  if (dateTo) {
    query.set('dateTo', String(dateTo));
  }
  const response = await apiRequest(`/notifications?${query.toString()}`, { method: 'GET', withAuth: true });
  return {
    items: Array.isArray(response.data) ? response.data : [],
    pagination: response.pagination || null,
    unreadCount: response?.meta?.unreadCount || 0,
  };
}

export async function markNotificationAsRead(notificationId) {
  const response = await apiRequest(`/notifications/${encodeURIComponent(notificationId)}/read`, {
    method: 'PATCH',
    withAuth: true,
  });
  const unreadCount = response?.meta?.unreadCount ?? response?.data?.unreadCount;
  return {
    ...(response.data || {}),
    unreadCount: typeof unreadCount === 'number' ? unreadCount : null,
  };
}

export async function trackNotificationInteraction(notificationId, interactionType = 'OPEN', platform = 'web') {
  await apiRequest(`/notifications/${encodeURIComponent(notificationId)}/interactions`, {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify({ interactionType, platform }),
  });
}

export async function markAllNotificationsAsRead() {
  const response = await apiRequest('/notifications/read-all', {
    method: 'PATCH',
    withAuth: true,
  });
  const unreadCount = response?.meta?.unreadCount ?? response?.data?.unreadCount;
  return {
    ...(response.data || {}),
    unreadCount: typeof unreadCount === 'number' ? unreadCount : null,
  };
}

export async function getNotificationPreferences() {
  const response = await apiRequest('/notifications/preferences', {
    method: 'GET',
    withAuth: true,
  });
  return response.data;
}

export async function patchNotificationPreferences(payload = {}) {
  const response = await apiRequest('/notifications/preferences', {
    method: 'PATCH',
    withAuth: true,
    body: JSON.stringify(payload),
  });
  return response.data;
}

export async function getWebPushPublicKey() {
  const response = await apiRequest('/notifications/webpush/public-key', {
    method: 'GET',
    withAuth: false,
  });
  return response?.data?.publicKey || null;
}

export async function subscribeWebPush(payload) {
  await apiRequest('/notifications/webpush/subscribe', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify(payload || {}),
  });
}

export async function unsubscribeWebPush(endpoint) {
  await apiRequest('/notifications/webpush/unsubscribe', {
    method: 'POST',
    withAuth: true,
    body: JSON.stringify({ endpoint }),
  });
}

export async function requestPasswordReset(email) {
  const response = await apiRequest('/auth/request-password-reset', {
    method: 'POST',
    withAuth: false,
    body: JSON.stringify({ email }),
  });
  if (!response.success) {
    throw new Error(response.message || 'Demande refusée.');
  }
  return response.message;
}

export async function verifyPasswordResetCode(email, code) {
  const response = await apiRequest('/auth/verify-password-reset-code', {
    method: 'POST',
    withAuth: false,
    body: JSON.stringify({
      email: typeof email === 'string' ? email.trim().toLowerCase() : '',
      code: typeof code === 'string' ? code.trim() : '',
    }),
  });
  if (!response.success || !response.data?.resetToken) {
    throw new Error(response.message || 'Code invalide ou expiré.');
  }
  return response.data.resetToken;
}

export async function resetPasswordWithToken(payload) {
  const response = await apiRequest('/auth/reset-password', {
    method: 'POST',
    withAuth: false,
    body: JSON.stringify(payload),
  });
  if (!response.success) {
    throw new Error(response.message || 'Réinitialisation impossible.');
  }
}

export async function apiRequest(path, options = {}) {
  const { withAuth = false, retryOnUnauthorized = true, ...requestOptions } = options;
  const headers = new Headers(requestOptions.headers || {});

  if (!headers.has('Content-Type') && requestOptions.body && !(requestOptions.body instanceof FormData)) {
    headers.set('Content-Type', 'application/json');
  }

  if (withAuth) {
    const accessToken = storage.getAccessToken();

    if (accessToken) {
      headers.set('Authorization', `Bearer ${accessToken}`);
    }
  }

  const response = await fetch(`${API_BASE_URL}${path}`, {
    ...requestOptions,
    headers,
  });

  if (response.status === 401 && withAuth && retryOnUnauthorized) {
    await refreshAccessToken();
    return apiRequest(path, {
      ...options,
      retryOnUnauthorized: false,
    });
  }

  return parseJsonResponse(response);
}

/**
 * Télécharge un fichier API avec le JWT courant (pour aperçu modale ou téléchargement).
 */
export async function fetchAuthenticatedFileBlob(apiPath, options = {}) {
  const accessToken = storage.getAccessToken();
  const headers = new Headers(options.headers || {});
  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }

  const response = await fetch(`${API_BASE_URL}${apiPath}`, { method: 'GET', headers });

  if (response.status === 401 && options.retryOnUnauthorized !== false) {
    await refreshAccessToken();
    return fetchAuthenticatedFileBlob(apiPath, { ...options, retryOnUnauthorized: false });
  }

  if (!response.ok) {
    let message = 'Impossible de charger le document.';
    const contentType = response.headers.get('content-type') || '';
    try {
      if (contentType.includes('json')) {
        const payload = await response.json();
        message = payload.message || message;
      } else if (response.status === 404) {
        message =
          'Guide PDF introuvable (404). Reconstruisez le backend : docker compose build backend && docker compose up -d backend';
      } else {
        message = `Erreur serveur (${response.status}). Vérifiez que l’API est à jour et accessible.`;
      }
    } catch {
      if (response.status === 404) {
        message =
          'Guide PDF introuvable (404). Reconstruisez le backend : docker compose build backend && docker compose up -d backend';
      }
    }
    throw new Error(message);
  }

  const blob = await response.blob();
  const contentType =
    response.headers.get('Content-Type')?.split(';')[0]?.trim() || blob.type || 'application/octet-stream';

  if (contentType.includes('json') || blob.type.includes('json')) {
    let message = 'Impossible de charger le document.';
    try {
      const payload = JSON.parse(await blob.text());
      message = payload.message || message;
    } catch {
      /* ignore */
    }
    throw new Error(message);
  }

  if (!blob.size) {
    throw new Error('Le fichier reçu est vide.');
  }

  const blobUrl = URL.createObjectURL(blob);
  return { blob, blobUrl, contentType };
}

/**
 * Télécharge un fichier API authentifié (PDF, etc.) via un lien temporaire.
 */
export async function downloadAuthenticatedFile(apiPath, filename) {
  const { blob, blobUrl } = await fetchAuthenticatedFileBlob(apiPath);
  const a = document.createElement('a');
  a.href = blobUrl;
  a.download = filename;
  a.rel = 'noopener';
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.setTimeout(() => URL.revokeObjectURL(blobUrl), 1000);
  return { blob };
}

/** Renouvelle le JWT en arrière-plan (épreuves longues > 15 min). */
export async function keepSessionAlive() {
  if (!storage.getRefreshToken()) return;
  try {
    await refreshAccessToken();
  } catch {
    /* échec silencieux — la prochaine requête API gèrera la session */
  }
}

export const authStorage = storage;
